<script setup lang="ts">
import { computed, reactive, ref } from "vue";

import {
  lookupPostcode,
  updateWorkPackLocation,
  type ProjectDetail,
  type WorkPackLocation,
  type WorkPackRow,
} from "@/api/client";
import LocationMap from "@/components/LocationMap.vue";

const props = defineProps<{
  pack: WorkPackRow;
  project?: ProjectDetail | null;
}>();
const emit = defineEmits<{ saved: [] }>();

const readOnly = computed(() => props.pack.status === "issued");
const loc = computed<WorkPackLocation>(() => props.pack.location ?? {});
const hasAny = computed(() =>
  Object.values(loc.value).some((v) => v != null && v !== ""),
);
const plottable = computed(
  () => loc.value.latitude != null && loc.value.longitude != null,
);

const editing = ref(false);
const busy = ref(false);
const errorMsg = ref<string | null>(null);
const lookupBusy = ref(false);

interface LocationForm {
  elr: string;
  mileage_from: string;
  mileage_to: string;
  line_id: string;
  track_id: string;
  access_point: string;
  location_name: string;
  postcode: string;
  latitude: number | null;
  longitude: number | null;
  notes: string;
}
const form = reactive<LocationForm>({
  elr: "",
  mileage_from: "",
  mileage_to: "",
  line_id: "",
  track_id: "",
  access_point: "",
  location_name: "",
  postcode: "",
  latitude: null,
  longitude: null,
  notes: "",
});

function startEdit(): void {
  const l = loc.value;
  form.elr = l.elr ?? "";
  form.mileage_from = l.mileage_from ?? "";
  form.mileage_to = l.mileage_to ?? "";
  form.line_id = l.line_id ?? "";
  form.track_id = l.track_id ?? "";
  form.access_point = l.access_point ?? "";
  form.location_name = l.location_name ?? "";
  form.postcode = l.postcode ?? "";
  form.latitude = l.latitude ?? null;
  form.longitude = l.longitude ?? null;
  form.notes = l.notes ?? "";
  errorMsg.value = null;
  editing.value = true;
}

async function resolvePostcode(): Promise<void> {
  if (!form.postcode.trim()) return;
  lookupBusy.value = true;
  errorMsg.value = null;
  try {
    const r = await lookupPostcode(form.postcode);
    form.postcode = r.postcode;
    form.latitude = r.latitude;
    form.longitude = r.longitude;
    if (!form.location_name) form.location_name = r.place_name;
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    lookupBusy.value = false;
  }
}

const projectHasLocation = computed(
  () => props.project?.latitude != null && props.project?.longitude != null,
);
function useProjectLocation(): void {
  if (!props.project || !projectHasLocation.value) return;
  form.postcode = props.project.postcode ?? form.postcode;
  form.location_name = props.project.location_name ?? form.location_name;
  form.latitude = props.project.latitude;
  form.longitude = props.project.longitude;
}

const clean = (v: string): string | null => (v.trim() ? v.trim() : null);
// v-model.number yields "" for a blank input — treat that as unset.
const num = (v: unknown): number | null =>
  v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v);

async function save(): Promise<void> {
  busy.value = true;
  errorMsg.value = null;
  try {
    await updateWorkPackLocation(props.pack.id, {
      elr: clean(form.elr)?.toUpperCase() ?? null,
      mileage_from: clean(form.mileage_from),
      mileage_to: clean(form.mileage_to),
      line_id: clean(form.line_id),
      track_id: clean(form.track_id),
      access_point: clean(form.access_point),
      location_name: clean(form.location_name),
      postcode: clean(form.postcode),
      latitude: num(form.latitude),
      longitude: num(form.longitude),
      notes: clean(form.notes),
    });
    editing.value = false;
    emit("saved");
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

const mileage = computed(() => {
  const a = loc.value.mileage_from;
  const b = loc.value.mileage_to;
  if (a && b) return `${a} → ${b}`;
  return a || b || null;
});

const inputCls =
  "rounded-md border border-neutral-300 px-3 py-2 text-sm";
</script>

<template>
  <section
    id="wp-sec-location"
    class="scroll-mt-4 rounded-xl border border-neutral-200 bg-neutral-0 p-5"
  >
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="flex items-center gap-2 font-semibold">
          Location on the railway
          <span
            class="rounded px-1.5 py-0.5 text-[10px] font-bold"
            :class="hasAny
              ? 'bg-semantic-success/10 text-semantic-success'
              : 'bg-semantic-warning/10 text-semantic-warning'"
          >{{ hasAny ? "✓" : "Required" }}</span>
        </h2>
        <p class="text-sm text-neutral-400">
          ELR, mileage and line the planner, PIC and COSS all brief from —
          plus a pin so the pack shows on the map.
        </p>
      </div>
      <button
        v-if="!readOnly && !editing"
        type="button"
        class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="startEdit"
      >
        {{ hasAny ? "Edit" : "+ Set location" }}
      </button>
    </div>

    <!-- Read view -->
    <div
      v-if="!editing"
      class="mt-3 grid gap-4 sm:grid-cols-[1fr_auto]"
    >
      <dl
        v-if="hasAny"
        class="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm"
      >
        <dt class="text-neutral-400">
          ELR
        </dt>
        <dd class="font-medium text-neutral-900">
          {{ loc.elr || "—" }}
        </dd>
        <dt class="text-neutral-400">
          Mileage
        </dt>
        <dd class="font-medium text-neutral-900">
          {{ mileage || "—" }}
        </dd>
        <dt class="text-neutral-400">
          Line / track
        </dt>
        <dd class="text-neutral-900">
          {{ [loc.line_id, loc.track_id].filter(Boolean).join(" · ") || "—" }}
        </dd>
        <dt class="text-neutral-400">
          Access point
        </dt>
        <dd class="text-neutral-900">
          {{ loc.access_point || "—" }}
        </dd>
        <dt class="text-neutral-400">
          Place
        </dt>
        <dd class="text-neutral-900">
          {{ [loc.location_name, loc.postcode].filter(Boolean).join(" · ") || "—" }}
        </dd>
        <template v-if="loc.notes">
          <dt class="text-neutral-400">
            Notes
          </dt>
          <dd class="whitespace-pre-line text-neutral-700">
            {{ loc.notes }}
          </dd>
        </template>
      </dl>
      <p
        v-else
        class="text-sm text-neutral-400"
      >
        No location yet.
      </p>
      <div
        v-if="plottable"
        class="w-full sm:w-72"
      >
        <LocationMap
          :lat="loc.latitude ?? null"
          :lng="loc.longitude ?? null"
          :label="pack.title"
          :zoom="13"
        />
      </div>
    </div>

    <!-- Edit form -->
    <div
      v-else
      class="mt-3 grid gap-3 rounded-lg bg-neutral-50 p-4"
    >
      <div class="grid gap-3 sm:grid-cols-4">
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>ELR</span>
          <input
            v-model="form.elr"
            type="text"
            placeholder="e.g. MLN1"
            maxlength="10"
            :class="inputCls"
            class="uppercase"
          >
        </label>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Mileage from</span>
          <input
            v-model="form.mileage_from"
            type="text"
            placeholder="12m 0440y"
            :class="inputCls"
          >
        </label>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Mileage to</span>
          <input
            v-model="form.mileage_to"
            type="text"
            placeholder="12m 0880y"
            :class="inputCls"
          >
        </label>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Line</span>
          <input
            v-model="form.line_id"
            type="text"
            placeholder="Up Main"
            :class="inputCls"
          >
        </label>
      </div>
      <div class="grid gap-3 sm:grid-cols-2">
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Track ID (optional)</span>
          <input
            v-model="form.track_id"
            type="text"
            placeholder="1100"
            :class="inputCls"
          >
        </label>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Access point</span>
          <input
            v-model="form.access_point"
            type="text"
            placeholder="AP 12 — Station Road"
            :class="inputCls"
          >
        </label>
      </div>
      <div class="grid gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Postcode (for the map pin)</span>
          <input
            v-model="form.postcode"
            type="text"
            placeholder="G1 1AA"
            :class="inputCls"
            @keyup.enter="resolvePostcode"
          >
        </label>
        <div class="flex items-end gap-2">
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-0 disabled:opacity-50"
            :disabled="lookupBusy || !form.postcode.trim()"
            @click="resolvePostcode"
          >
            {{ lookupBusy ? "Looking up…" : "Look up" }}
          </button>
          <button
            v-if="projectHasLocation"
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-0"
            title="Copy the project's site location"
            @click="useProjectLocation"
          >
            Use project site
          </button>
        </div>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Place name</span>
          <input
            v-model="form.location_name"
            type="text"
            placeholder="Shown on the pack and the map"
            :class="inputCls"
          >
        </label>
      </div>
      <div class="grid gap-3 sm:grid-cols-2">
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Latitude</span>
          <input
            v-model.number="form.latitude"
            type="number"
            step="any"
            min="-90"
            max="90"
            :class="inputCls"
          >
        </label>
        <label class="grid gap-1 text-xs text-neutral-500">
          <span>Longitude</span>
          <input
            v-model.number="form.longitude"
            type="number"
            step="any"
            min="-180"
            max="180"
            :class="inputCls"
          >
        </label>
      </div>
      <label class="grid gap-1 text-xs text-neutral-500">
        <span>Notes (limited clearance, OLE / conductor rail, structures…)</span>
        <textarea
          v-model="form.notes"
          rows="2"
          :class="inputCls"
        />
      </label>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="busy"
          @click="save"
        >
          {{ busy ? "Saving…" : "Save location" }}
        </button>
        <button
          type="button"
          class="text-sm text-neutral-500 hover:text-neutral-900"
          @click="editing = false"
        >
          Cancel
        </button>
        <span
          v-if="errorMsg"
          class="text-sm text-semantic-danger"
        >{{ errorMsg }}</span>
      </div>
      <p
        v-if="pack.status === 'verified' || pack.status === 'authorised'"
        class="text-xs text-semantic-warning"
      >
        Saving a change supersedes this version — the pack goes back to draft
        and must be verified and authorised again.
      </p>
    </div>
  </section>
</template>
